import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';
import { useAuth } from '@/app/contexts/AuthContext';
import { getSoundMutedPreference, getSoundMutedStorageKey, setSoundMutedPreference } from '@/lib/notifications/soundPreferences';
import { playNotificationSound } from '@/lib/notifications/sounds';
import { clearGuildChatMessages, deleteGuildChatMessage, listGuildChatMessages, sendGuildChatMessage } from '@/lib/repositories/chatRepository';
import { supabase } from '@/lib/supabase/client';
import type { GuildChatMessage } from '@/types/domain';

interface GuildChatContextValue {
  messages: GuildChatMessage[];
  isLoading: boolean;
  error: string | null;
  isCollapsed: boolean;
  isMuted: boolean;
  sendMessage: (content: string) => Promise<void>;
  deleteMessage: (messageId: string) => Promise<void>;
  clearMessages: () => Promise<void>;
  toggleCollapsed: () => void;
  toggleMuted: () => void;
}

const GuildChatContext = createContext<GuildChatContextValue | null>(null);
const COLLAPSED_KEY = 'woof-woof:guild-chat:collapsed';
const MUTED_KEY = getSoundMutedStorageKey();

export function GuildChatProvider({ children }: PropsWithChildren): JSX.Element {
  const { authMode, profile, isApproved, isSignedIn, needsOnboarding, hasAnyRole } = useAuth();
  const [messages, setMessages] = useState<GuildChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const knownMessageIdsRef = useRef<Set<string>>(new Set());
  const isStaffChatUser = hasAnyRole(['owner', 'admin', 'officer', 'mass_manager']);
  const canAccessGuildChat = isSignedIn && !needsOnboarding && (isApproved || isStaffChatUser);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const storedCollapsed = window.localStorage.getItem(COLLAPSED_KEY);
    const defaultCollapsed = window.matchMedia('(max-width: 1120px)').matches;
    setIsCollapsed(storedCollapsed === null ? defaultCollapsed : storedCollapsed === 'true');
    setIsMuted(getSoundMutedPreference());
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(COLLAPSED_KEY, String(isCollapsed));
  }, [isCollapsed]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    setSoundMutedPreference(isMuted);
  }, [isMuted]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== MUTED_KEY) {
        return;
      }

      setIsMuted(getSoundMutedPreference());
    };

    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  const loadMessages = useCallback(async () => {
    if (authMode !== 'supabase' || !canAccessGuildChat) {
      setMessages([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const nextMessages = await listGuildChatMessages();
      setMessages(nextMessages);
      knownMessageIdsRef.current = new Set(nextMessages.map((message) => message.id));
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Failed to load guild chat.');
    } finally {
      setIsLoading(false);
    }
  }, [authMode, canAccessGuildChat]);

  useEffect(() => {
    void loadMessages();
  }, [loadMessages]);

  useEffect(() => {
    if (authMode !== 'supabase' || !supabase || !canAccessGuildChat) {
      return undefined;
    }

    const client = supabase;

    const channel = client
      .channel('guild-chat')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'guild_chat_messages' }, (payload) => {
        if (payload.eventType === 'INSERT') {
          const nextMessage = payload.new as {
            id: string;
            profile_id: string;
            content: string;
            created_at: string;
            updated_at: string;
          };

          if (!nextMessage?.id || knownMessageIdsRef.current.has(nextMessage.id)) {
            return;
          }

          knownMessageIdsRef.current.add(nextMessage.id);
          setMessages((current) => [
            ...current,
            {
              id: nextMessage.id,
              profileId: nextMessage.profile_id,
              content: nextMessage.content,
              createdAt: nextMessage.created_at,
              updatedAt: nextMessage.updated_at,
            },
          ]);

          if (!isMuted && nextMessage.profile_id !== profile?.id) {
            void playNotificationSound('guild-chat');
          }

          return;
        }

        if (payload.eventType === 'DELETE') {
          const deletedMessage = payload.old as { id?: string };

          if (!deletedMessage?.id) {
            return;
          }

          knownMessageIdsRef.current.delete(deletedMessage.id);
          setMessages((current) => current.filter((message) => message.id !== deletedMessage.id));
          return;
        }

        if (payload.eventType === 'UPDATE') {
          const updatedMessage = payload.new as {
            id?: string;
            content?: string;
            updated_at?: string;
          };

          if (!updatedMessage?.id) {
            return;
          }

          setMessages((current) =>
            current.map((message) =>
              message.id === updatedMessage.id
                ? {
                    ...message,
                    content: updatedMessage.content ?? message.content,
                    updatedAt: updatedMessage.updated_at ?? message.updatedAt,
                  }
                : message,
            ),
          );
        }
      })
      .subscribe();

    return () => {
      void client.removeChannel(channel);
    };
  }, [authMode, canAccessGuildChat, isMuted, profile?.id]);

  const value = useMemo<GuildChatContextValue>(
    () => ({
      messages,
      isLoading,
      error,
      isCollapsed,
      isMuted,
      sendMessage: async (content) => {
        if (!profile || !canAccessGuildChat) {
          return;
        }

        await sendGuildChatMessage(profile.id, content);
      },
      deleteMessage: async (messageId) => {
        await deleteGuildChatMessage(messageId);
        knownMessageIdsRef.current.delete(messageId);
        setMessages((current) => current.filter((message) => message.id !== messageId));
      },
      clearMessages: async () => {
        await clearGuildChatMessages();
        knownMessageIdsRef.current.clear();
        setMessages([]);
      },
      toggleCollapsed: () => setIsCollapsed((current) => !current),
      toggleMuted: () => setIsMuted((current) => !current),
    }),
    [canAccessGuildChat, error, isCollapsed, isLoading, isMuted, messages, profile],
  );

  return <GuildChatContext.Provider value={value}>{children}</GuildChatContext.Provider>;
}

export function useGuildChat(): GuildChatContextValue {
  const context = useContext(GuildChatContext);

  if (!context) {
    throw new Error('useGuildChat must be used within GuildChatProvider');
  }

  return context;
}
